import React from 'react';
import './LongTermScheduleCalendar.scss';
import {
    LongTermScheduleDayShifts,
    LongTermSchedulePseudoStates,
} from './LongTermSchedule';
import Select from 'react-select';
import moment from 'moment';
import LongTermScheduleKey from './LongTermScheduleKey';
import { Can } from '../Can';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretLeft, faCaretRight } from '@fortawesome/free-solid-svg-icons';

const LtsViews = ['week', 'month', '5week', 'custom'];

const LtsViewLabels = {
    week: 'Week',
    month: 'Month',
    '5week': '5 Wk',
    custom: 'Custom',
};

export default class LongTermScheduleToolbar extends React.Component {
    getRangeLabel = () => {
        const { days } = this.props;
        if (!days.length) {
            return '';
        }

        const s = moment(days[0]);
        const e = moment(days[days.length - 1]);

        if (s.month() === e.month()) {
            return `${s.format('MMMM')} ${s.date()} \u2013 ${e.date()}, ${s.year()}`;
        }

        return `${s.format('MMM')} ${s.date()} \u2013 ${e.format('MMM')} ${e.date()}, ${e.year()}`;
    };

    getLtsStateOptions = () =>
        Object.entries(LongTermSchedulePseudoStates).map(([key, value]) => ({
            value,
            label: key.replace(/([A-Z])/g, ' $1').trim(),
        }));

    render() {
        const {
            viewMode,
            customStart,
            customEnd,
            divisions,
            divisionIds,
            shiftId,
            ltsStateIds,
            onAddLongTermSchedule,
            onCustomEndChange,
            onCustomStartChange,
            onDivisionChange,
            onShiftChange,
            onStateChange,
            onViewChange,
            onNavigate,
            goToday,
            searchFilter,
            onSearchFilterChange,
            isAdmin,
            showAll,
            onShowAllChange,
            loading,
        } = this.props;

        const rangeLabel = this.getRangeLabel();
        const ltsStateOptions = this.getLtsStateOptions();
        const selectedDivisions = (divisions || []).filter((d) =>
            (divisionIds || []).includes(d.id)
        );
        const selectedStates = ltsStateOptions.filter((s) =>
            (ltsStateIds || []).includes(s.value)
        );

        // https://github.com/JedWatson/react-select/discussions/4620
        const customStyles = {
            control: (provided, state) => ({
                ...provided,
                background: '#fff',
                borderColor: '#9e9e9e',
                minHeight: '28px',
                height: '28px',
                boxShadow: state.isFocused ? null : null,
            }),
            valueContainer: (provided) => ({
                ...provided,
                height: '28px',
                padding: '0 6px',
            }),

            input: (provided) => ({
                ...provided,
                margin: '0px',
            }),
            indicatorSeparator: () => ({
                display: 'none',
            }),
            indicatorsContainer: (provided) => ({
                ...provided,
                height: '28px',
            }),
        };

        const selectedShift = LongTermScheduleDayShifts.find(
            (lts) => lts.id == shiftId
        );

        return (
            <div className="lts-toolbar">
                <div className="lts-toolbar-left">
                    <div className="lts-nav-group">
                        <button
                            className="lts-nav-btn"
                            onClick={() => onNavigate(-1)}
                            disabled={loading}
                        >
                            <FontAwesomeIcon icon={faCaretLeft} />
                        </button>
                        <button
                            className="lts-today-btn"
                            onClick={() => goToday()}
                            disabled={loading}
                        >
                            TODAY
                        </button>
                        <button
                            className="lts-nav-btn"
                            onClick={() => onNavigate(1)}
                            disabled={loading}
                        >
                            <FontAwesomeIcon icon={faCaretRight} />
                        </button>
                    </div>

                    <div className="lts-divider" />

                    <div className="lts-filter-group">
                        <label
                            className="lts-filter-label my-auto"
                            htmlFor="lts-divisions"
                        >
                            Divisions
                        </label>
                        <Select
                            id="lts-divisions"
                            className="lts-react-select lts-division-select"
                            isMulti
                            options={divisions || []}
                            value={selectedDivisions}
                            onChange={onDivisionChange}
                            getOptionLabel={(d) => d.name}
                            getOptionValue={(d) => d.id}
                            placeholder="All"
                            styles={customStyles}
                            isDisabled={loading}
                        />
                    </div>

                    <div className="lts-filter-group">
                        <label
                            className="lts-filter-label my-auto"
                            htmlFor="lts-shift"
                        >
                            Shift
                        </label>
                        <Select
                            id="lts-shift"
                            className="lts-react-select lts-shift-select"
                            options={LongTermScheduleDayShifts}
                            getOptionValue={(s) => s.id}
                            value={selectedShift}
                            getOptionLabel={(s) => s.name}
                            placeholder="All"
                            styles={customStyles}
                            onChange={onShiftChange}
                            isDisabled={loading}
                        />
                    </div>

                    <div className="lts-filter-group">
                        <label
                            className="lts-filter-label my-auto"
                            htmlFor="lts-status"
                        >
                            Status
                        </label>
                        <Select
                            inputid="lts-status"
                            className="lts-react-select"
                            isMulti
                            options={ltsStateOptions}
                            value={selectedStates}
                            onChange={onStateChange}
                            placeholder="All"
                            styles={customStyles}
                            isDisabled={loading}
                        />
                    </div>

                    <div className="lts-filter-group">
                        <label
                            className="lts-filter-label my-auto"
                            htmlFor="search-filter"
                        >
                            Filter
                        </label>

                        <input
                            type="text"
                            inputid="search-filter"
                            className="lts-search-filter"
                            value={searchFilter}
                            onChange={onSearchFilterChange}
                            placeholder="None active"
                            disabled={loading}
                        />
                    </div>

                    {!isAdmin && (
                        <div className="lts-filter-group">
                            <label
                                className="lts-filter-label my-auto"
                                htmlFor="lts-show-all"
                            >
                                All
                            </label>
                            <input
                                id="lts-show-all"
                                type="checkbox"
                                checked={Boolean(showAll)}
                                onChange={onShowAllChange}
                                disabled={loading}
                            />
                        </div>
                    )}

                    <div className="lts-divider" />

                    <LongTermScheduleKey {...this.props} />
                </div>

                <div className="lts-toolbar-right">
                    <span className="lts-range-label">{rangeLabel}</span>
                    <div className="lts-divider" />

                    <div className="lts-view-toggle">
                        {LtsViews.map((v) => (
                            <button
                                key={v}
                                className={`lts-view-btn${viewMode === v ? ' active' : ''}`}
                                onClick={onViewChange.bind(this, {
                                    viewMode: v,
                                })}
                                disabled={loading}
                            >
                                {LtsViewLabels[v]}
                            </button>
                        ))}
                    </div>

                    {viewMode === 'custom' && (
                        <div className="lts-custom-dates">
                            <input
                                type="date"
                                value={customStart}
                                onChange={onCustomStartChange}
                                disabled={loading}
                            />
                            <span>to</span>
                            <input
                                type="date"
                                value={customEnd}
                                onChange={onCustomEndChange}
                                disabled={loading}
                            />
                        </div>
                    )}

                    <Can I="create" a="longtermschedule">
                        <div className="lts-divider" />

                        <button
                            className="lts-add-btn"
                            onClick={onAddLongTermSchedule}
                            disabled={loading}
                        >
                            <span className="lts-plus">+</span> Add Job
                        </button>
                    </Can>
                </div>
            </div>
        );
    }
}
